import React, { useState, useEffect } from 'react';
import { RulesRegulationsService } from '../../../../services/rulesRegulationsService';

const AcknowledgementsModal = ({ isOpen, onClose, rule }) => {
  const [activeTab, setActiveTab] = useState('acknowledgements');
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [versions, setVersions] = useState([]);
  const [stats, setStats] = useState({ total: 0, ack: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && rule) {
      setActiveTab('acknowledgements');
      fetchData();
    }
  }, [isOpen, rule]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    const ruleId = rule.id || rule.Id;
    try {
      // 1. Fetch Acknowledgements
      const ackResponse = await RulesRegulationsService.getAcknowledgements(ruleId);
      const ackData = ackResponse.data || ackResponse.Data || ackResponse;
      
      setStats({
        total: ackResponse.totalUsers || ackResponse.TotalUsers || 0,
        ack: ackResponse.totalAcknowledged || ackResponse.TotalAcknowledged || 0,
        pending: ackResponse.totalPending || ackResponse.TotalPending || 0
      });
      setAcknowledgements(Array.isArray(ackData) ? ackData : []);

      // 2. Fetch Full Details for Versions List
      try {
        const detailResponse = await RulesRegulationsService.getRuleById(ruleId);
        const detailData = detailResponse.data || detailResponse.Data || detailResponse;
        setVersions(detailData.versions || detailData.Versions || []);
      } catch (detErr) {
        setVersions(rule.versions || rule.Versions || []);
      }
    } catch (err) {
      setError("Failed to load rule details.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentVersion = rule.currentVersion || rule.CurrentVersion || 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-fadeIn">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-800">Rule Details & Compliance</h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                v{currentVersion}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Rule: {rule.title || rule.Title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Modal Tabs */}
        <div className="flex border-b border-gray-200 px-6 bg-white shrink-0">
          <button
            onClick={() => setActiveTab('acknowledgements')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'acknowledgements' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            User Acknowledgements ({stats.ack}/{stats.total})
          </button>
          <button
            onClick={() => setActiveTab('versions')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'versions' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Version History ({versions.length})
          </button>
        </div>

        {/* Tab 1: Acknowledgements */}
        {activeTab === 'acknowledgements' && (
          <>
            {stats.total > 0 && (
              <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-white border-b border-gray-100 shrink-0">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="text-xs font-semibold text-blue-600 uppercase">Total Affected Users</div>
                  <div className="text-xl font-bold text-blue-900">{stats.total}</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-xs font-semibold text-green-600 uppercase">Acknowledged (v{currentVersion})</div>
                  <div className="text-xl font-bold text-green-900">{stats.ack}</div>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <div className="text-xs font-semibold text-orange-600 uppercase">Pending</div>
                  <div className="text-xl font-bold text-orange-900">{stats.pending}</div>
                </div>
              </div>
            )}
            
            <div className="p-6 overflow-y-auto flex-1 bg-white">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : error ? (
                <div className="text-center text-red-500 py-8 font-medium">{error}</div>
              ) : acknowledgements.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  No acknowledgements found for this rule version yet.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acknowledged On</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {acknowledgements.map((ack, index) => (
                      <tr key={ack.userId || ack.UserId || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ack.userName || ack.UserName || "Unknown User"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ack.mobileNumber || ack.MobileNumber || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(ack.isAcknowledged ?? ack.IsAcknowledged) ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Acknowledged</span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Pending</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(ack.acknowledgedOn || ack.AcknowledgedOn) 
                            ? new Date(ack.acknowledgedOn || ack.AcknowledgedOn).toLocaleString() 
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Tab 2: Version History */}
        {activeTab === 'versions' && (
          <div className="p-6 overflow-y-auto flex-1 bg-white">
            {versions.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                No previous snapshots recorded. (Rule has current version v{currentVersion})
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((ver, idx) => {
                  const verNumber = ver.versionNumber || ver.VersionNumber;
                  const isLatest = verNumber === currentVersion;
                  return (
                    <div 
                      key={ver.id || ver.Id || idx}
                      className={`p-4 rounded-lg border ${isLatest ? 'border-indigo-200 bg-indigo-50/40' : 'border-gray-200 bg-white'} transition-all`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isLatest ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                            v{verNumber}
                          </span>
                          <span className="text-sm font-semibold text-gray-800">{ver.title || ver.Title}</span>
                          {isLatest && (
                            <span className="text-[11px] font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">
                              Current Active Version
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {ver.createdOn || ver.CreatedOn ? new Date(ver.createdOn || ver.CreatedOn).toLocaleString() : ''}
                        </span>
                      </div>
                      {ver.description && (
                        <p className="text-xs text-gray-600 mt-2 line-clamp-2">{ver.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
          <button 
            onClick={onClose}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-5 py-2 rounded-lg font-medium shadow-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcknowledgementsModal;
